import { styled } from '@ghq-abi/design-system';
import React from 'react';

const ModalOverlay = styled('div', {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.6)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 99999,
  padding: '$4',
  backdropFilter: 'blur(2px)',
});

const ModalContainer = styled('div', {
  backgroundColor: '$white',
  borderRadius: '8px',
  maxWidth: '90vw',
  maxHeight: '90vh',
  display: 'flex',
  flexDirection: 'column',
  boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
  border: '1px solid $gray200',
  position: 'relative',

  variants: {
    size: {
      small: {
        width: 'calc(100wv - 32px)',
        padding: '16px',
        alignItems: 'center',
        textAlign: 'center',
      },
      medium: {
        width: '650px',
        '@md': {
          maxWidth: '85vw',
          width: '800px',
        },
      },
      large: {
        width: '900px',
        '@md': {
          maxWidth: '80vw',
          width: '1000px',
        },
      },
    },
  },

  defaultVariants: {
    size: 'small',
  },
});

const CloseButton = styled('button', {
  position: 'absolute',
  top: '$4',
  right: '$4',
  backgroundColor: 'transparent',
  border: 'none',
  color: '$gray400',
  cursor: 'pointer',
  padding: '$2',
  borderRadius: '6px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: 'all 0.2s ease',

  '&:hover': {
    color: '$gray600',
    backgroundColor: '$gray100',
    transform: 'scale(1.05)',
  },

  '&:active': {
    transform: 'scale(0.95)',
  },

  variants: {
    size: {
      small: {
        top: '$4',
        right: '$4',
      },
      medium: {
        top: '$4',
        right: '$4',
      },
      large: {
        right: '$6',
        transform: 'translateY(-50%)',
        top: '50%',
      },
    },
  },

  defaultVariants: {
    size: 'small',
  },
});

export interface BaseModalProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  showCloseButton?: boolean;
  size?: 'small' | 'medium' | 'large';
}

export const BaseModal: React.FC<BaseModalProps> = ({
  isOpen,
  onClose,
  children,
  showCloseButton = true,
  size = 'small',
}) => {
  React.useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  React.useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <ModalOverlay onClick={() => { }}>
      <ModalContainer size={size} onClick={e => e.stopPropagation()}>
        {showCloseButton && (
          <CloseButton size={size} onClick={onClose}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path
                d="M18 6L6 18M6 6l12 12"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </CloseButton>
        )}
        {children}
      </ModalContainer>
    </ModalOverlay >
  );
};

export { CloseButton, ModalContainer, ModalOverlay };
